import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Users, TrendingDown, TrendingUp, LayoutDashboard, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { Label } from '@/components/ui/label';
import { Toggle } from '@/components/ui/toggle';
import { PaymentService } from '@/services/PaymentService';
import { useAuth } from '@/hooks/useAuth';

interface PaymentHistory {
  date: string;
  amount: number;
  status: 'pago' | 'falha';
  description?: string;
}

interface PlanChange {
  date: string;
  oldPlan: string;
  newPlan: string;
  oldAmount: number;
  newAmount: number;
  type: 'initial' | 'upgrade' | 'downgrade';
}

interface Plan {
  id: string;
  title: string;
  price: number;
  description: string;
  is_custom?: boolean;
  device_limit?: number | null;
  max_devices?: number | null;
  QT_cupom?: number | null;
}

interface ClientFinance {
  id: string;
  name: string;
  email: string;
  plan: string;
  status: string;
  screensCount: number;
  mrr: number;
  createdAt: string;
  lastActivity: string;
  planExpirationDate: string | null;
  paymentStatus: 'ativo' | 'inadimplente' | 'cancelado';
  paymentHistory: PaymentHistory[];
  playersEnabled: boolean;
  totalRevenue: number;
  arrProjection: number;
  planChanges: PlanChange[];
  avatar_url?: string | null;
  deviceLimit?: number | null;
  promoLimit?: number | null;
  promotionsUsed?: number;
  devicesUsed?: number;
  screensUsed?: number;
  devicesStatus?: Array<{ id: string; status: string }>;
}

interface RealClient {
  id: string;
  email: string;
  full_name: string;
  plan_id: string | null;
  subscription_status: string | null;
  subscription_start_date: string | null;
  subscription_end_date: string | null;
  created_at: string;
  updated_at: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  role?: string | null; // Campo para identificar motoristas
  avatar_url?: string | null;
}

const statusColor: Record<string, string> = {
  ativo: 'text-white bg-green-600',
  inadimplente: 'text-white bg-red-600',
  cancelado: 'text-gray-600 bg-white border border-gray-300',
  testando: 'text-white bg-blue-500',
};

const paymentStatusLabel: Record<string, string> = {
  ativo: 'Em dia',
  inadimplente: 'Inadimplente',
  cancelado: 'Cancelado',
  testando: 'Testando',
};

const ClientFinanceSection: React.FC = () => {
  const [selectedClient, setSelectedClient] = useState<ClientFinance | null>(null);

  const handleClientSelect = (client: ClientFinance) => {
    setSelectedClient(client);
    setSelectedPlan('');
    setIsSubscriptionActive(client.status === 'ativo');
  };
  const [clients, setClients] = useState<ClientFinance[]>([]);
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [isSubscriptionActive, setIsSubscriptionActive] = useState(false);
  const { toast } = useToast();
  const { user: masterUser } = useAuth();
  const [isCustomSelected, setIsCustomSelected] = useState(false);
  const [customDeviceLimit, setCustomDeviceLimit] = useState<string>('');
  const [customQtCupom, setCustomQtCupom] = useState<string>('');
  const [customPrice, setCustomPrice] = useState<string>('');
  const [customErrors, setCustomErrors] = useState<{ device?: string; qt?: string; price?: string }>({});
  const [filterStart, setFilterStart] = useState<string>('');
  const [filterEnd, setFilterEnd] = useState<string>('');
  const [filterType, setFilterType] = useState<'all' | 'pago' | 'falha'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  console.log('🔄 Estado inicial - clients:', clients);
  console.log('🔄 Estado inicial - loading:', loading);

  // Sistema de atualização automática a cada 30 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('🔄 ClientFinanceSection - Atualização automática...');
      fetchRealClients();
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, []);

  // Função para buscar clientes reais do banco de dados
  const fetchRealClients = async () => {
    console.log('🚀 Função fetchRealClients chamada!');
    setLoading(true);

    try {
      console.log('🔍 Buscando perfis com emails...');

      // Buscar todos os perfis (usuários reais), incluindo usuários trial
      const { data: profilesWithEmails, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .neq('email', 'master@admin.com')
        .order('created_at', { ascending: false });

      if (profilesError) {
        console.error('❌ Erro ao buscar perfis:', profilesError);
        toast({
          title: "❌ Erro",
          description: "Erro ao carregar clientes",
          variant: "destructive",
        });
        return;
      }

      console.log('📋 Dados dos perfis encontrados:', profilesWithEmails);

      // Buscar IDs de todos os motoristas para excluí-los da listagem
      // Motoristas usam o Companion e não devem aparecer na seção de clientes finais
      const { data: drivers, error: driversError } = await supabase
        .from('drivers')
        .select('id');

      // Criar um Set com os IDs dos motoristas para verificação rápida
      const driverIds = new Set(drivers?.map(d => d.id) || []);

      if (driversError) {
        console.warn('[ClientFinanceSection] Aviso ao buscar motoristas:', driversError);
      }

      // Filtrar perfis para excluir motoristas
      const profilesWithoutDrivers = profilesWithEmails?.filter(profile => !driverIds.has(profile.id)) || [];
      console.log(`[ClientFinanceSection] Total perfis: ${profilesWithEmails?.length}, Motoristas: ${driverIds.size}, Clientes finais: ${profilesWithoutDrivers.length}`);

      if (!profilesWithoutDrivers || profilesWithoutDrivers.length === 0) {
        console.log('⚠️ Nenhum perfil encontrado (excluindo motoristas)');
        setClients([]);
        return;
      }

      // Filtrar usuários válidos (incluindo trial)
      const validProfiles = profilesWithoutDrivers.filter((profile: RealClient) => {
        // Excluir usuários que são motoristas (role = 'driver')
        // Isso pega motoristas que tiveram a tabela drivers deletada mas auth.users permanece
        if (profile.role && profile.role.toLowerCase() === 'driver') {
          console.log(`⛔ Excluindo motorista do gerenciamento de clientes: ${profile.email}`);
          return false;
        }

        const isTrialUser = profile.subscription_plan &&
          (profile.subscription_plan.toLowerCase().includes('trial') ||
            profile.subscription_plan.toLowerCase().includes('gratuito'));

        const isTrialByEmail = profile.email === 'user@letswatch.com.br'; // User trial específico

        const isRegularUser = profile.id &&
          profile.email &&
          profile.email !== 'master@admin.com' &&
          profile.email !== 'N/A' &&
          profile.email !== '' &&
          profile.email.includes('@') &&
          profile.full_name &&
          profile.full_name !== '';

        const isValidUser = isRegularUser || isTrialUser || isTrialByEmail;

        console.log(`🔍 Verificando perfil ${profile.full_name || 'Sem nome'} (${profile.email || 'Sem email'}) - Trial: ${isTrialUser || isTrialByEmail} | Regular: ${isRegularUser} | Válido: ${isValidUser}`);
        return isValidUser;
      });

      console.log(`✅ Perfis válidos encontrados: ${validProfiles.length} de ${profilesWithoutDrivers.length}`);

      if (validProfiles.length === 0) {
        console.log('⚠️ Nenhum perfil válido encontrado após filtro');
        setClients([]);
        return;
      }

      // Converter perfis válidos para o formato ClientFinance
      const realClients: ClientFinance[] = validProfiles.map((profile: RealClient) => {
        // Determinar se é usuário trial
        const isTrialUser = (profile.subscription_plan &&
          (profile.subscription_plan.toLowerCase().includes('trial') ||
            profile.subscription_plan.toLowerCase().includes('gratuito'))) ||
          profile.email === 'user@letswatch.com.br';

        // Determinar status de pagamento baseado no subscription_status e subscription_end_date
        let paymentStatus: 'ativo' | 'inadimplente' | 'cancelado' = 'cancelado';
        let isPlanActive = false;
        let statusReason = '';

        // Para usuários trial, status sempre ativo se dentro do período
        if (isTrialUser) {
          if (profile.subscription_status === 'active') {
            // Usar subscription_end_date ou subscription_expires_at como fallback
            const expirationDate = profile.subscription_end_date || (profile as any).subscription_expires_at;
            if (expirationDate) {
              const endDate = new Date(expirationDate);
              const now = new Date();
              isPlanActive = endDate > now;

              if (isPlanActive) {
                paymentStatus = 'ativo';
                statusReason = `Trial ativo até ${endDate.toLocaleDateString('pt-BR')}`;
              } else {
                paymentStatus = 'cancelado';
                statusReason = `Trial expirado em ${endDate.toLocaleDateString('pt-BR')}`;
              }
            } else {
              // Trial sem data de expiração - considerar ativo
              isPlanActive = true;
              paymentStatus = 'ativo';
              statusReason = 'Trial ativo (sem data de expiração)';
            }
          } else {
            paymentStatus = 'cancelado';
            statusReason = 'Trial cancelado';
          }
        } else {
          // Lógica original para usuários com planos pagos
          if (profile.subscription_status === 'active') {
            // Verificar se o plano não expirou - usar subscription_end_date ou subscription_expires_at
            const expirationDate = profile.subscription_end_date || (profile as any).subscription_expires_at;
            if (expirationDate) {
              const endDate = new Date(expirationDate);
              const now = new Date();
              isPlanActive = endDate > now;

              if (isPlanActive) {
                paymentStatus = 'ativo';
                statusReason = 'Plano ativo e não expirado';
              } else {
                paymentStatus = 'inadimplente';
                statusReason = 'Plano expirado em ' + endDate.toLocaleDateString('pt-BR');
              }
            } else {
              // Se não tem data de expiração, considerar ativo se status é active
              isPlanActive = true;
              paymentStatus = 'ativo';
              statusReason = 'Plano ativo (sem data de expiração)';
            }
          } else if (profile.subscription_status === 'cancelled' || profile.subscription_status === 'canceled') {
            paymentStatus = 'cancelado';
            statusReason = 'Plano cancelado';
          } else {
            paymentStatus = 'cancelado';
            statusReason = 'Sem plano ativo';
          }
        }

        // Buscar informações do plano
        let plan = 'Sem Plano';
        let mrr = 0;
        let arrProjection = 0;
        let planChanges: PlanChange[] = [];

        // Usar dados do perfil diretamente se disponíveis
        if (profile.subscription_plan) {
          plan = profile.subscription_plan;
        }

        // Converter plan_price para número, considerando que pode vir como string
        const profilePrice = profile.plan_price;
        if (profilePrice) {
          if (typeof profilePrice === 'string') {
            mrr = parseFloat(profilePrice);
          } else {
            mrr = Number(profilePrice);
          }
          arrProjection = mrr * 12; // ARR = MRR * 12
        }

        console.log(`👤 Processando cliente ${profile.email || 'Sem email'}:`, {
          plan,
          mrr,
          status: paymentStatus,
          reason: statusReason,
          isTrialUser: isTrialUser,
          profileData: {
            subscription_status: profile.subscription_status,
            subscription_plan: profile.subscription_plan,
            plan_price: profile.plan_price,
            subscription_end_date: profile.subscription_end_date
          }
        });

        return {
          id: profile.id,
          name: profile.full_name || 'Cliente Sem Nome',
          email: profile.email || 'Email não informado',
          plan: plan,
          status: paymentStatus,
          screensCount: 0, // Será calculado depois
          mrr: mrr,
          createdAt: profile.subscription_start_date || profile.created_at,
          lastActivity: profile.updated_at,
          planExpirationDate: profile.subscription_end_date || (profile as any).subscription_expires_at,
          paymentStatus: paymentStatus,
          avatar_url: profile.avatar_url,
          paymentHistory: [], // Será preenchido posteriormente
          playersEnabled: true, // Por padrão, players estão ativos
          totalRevenue: 0, // Será calculado
          arrProjection: arrProjection,
          planChanges: planChanges,
          deviceLimit: isTrialUser ? 1 : null, // Trial tem limite de 1 dispositivo
          promoLimit: isTrialUser ? 1 : ((profile as any).QT_cupom_User ?? null), // Trial tem limite de 1 promoção
          promotionsUsed: 0,
          devicesUsed: 0,
          screensUsed: 0,
        };
      });

      // Buscar histórico de pagamentos e estatísticas para cada cliente
      const clientsWithFullStats = await Promise.all(
        realClients.map(async (client) => {
          try {
            // Buscar histórico de pagamentos real
            const paymentHistory = await fetchPaymentHistory(client.id);

            // Calcular ganhos totais baseado nos pagamentos reais
            const totalRevenue = calculateTotalRevenue(
              paymentHistory,
              client.paymentStatus
            );

            // Recalcular ARR projetado com histórico real
            const arrProjectionCalc = calculateARRProjection(
              paymentHistory,
              client.mrr,
              client.createdAt,
              client.paymentStatus,
              client.planExpirationDate
            );

            // Buscar telas do cliente
            let screensCount = 0;
            try {
              const { data: screensRpc } = await supabase
                .rpc('admin_get_user_screens', { p_user_id: client.id });
              if (screensRpc && Array.isArray(screensRpc)) {
                screensCount = (screensRpc as any[]).length;
              } else {
                // Fallback: tentar descobrir organização e contar por organization_id
                const { data: orgMap } = await supabase
                  .from('organizations_users')
                  .select('organization_id')
                  .eq('user_id', client.id)
                  .limit(1);
                const orgId = orgMap && orgMap[0]?.organization_id;
                if (orgId) {
                  const { data: screensByOrg } = await supabase
                    .from('screens')
                    .select('id')
                    .eq('organization_id', orgId);
                  screensCount = (screensByOrg || []).length;
                } else {
                  const { data: screensByUser } = await supabase
                    .from('screens')
                    .select('id')
                    .eq('user_id', client.id);
                  screensCount = (screensByUser || []).length;
                }
              }
            } catch { }
            // Buscar dispositivos do cliente via RPC admin para evitar RLS
            let devicesData: any[] = [];
            try {
              const { data: devRpc } = await supabase.rpc('admin_get_user_devices', { p_user_id: client.id });
              if (devRpc && Array.isArray(devRpc)) devicesData = devRpc as any[];
            } catch { }

            // Buscar promoções do cliente
            const { data: promosData } = await supabase
              .from('promos')
              .select('id, active')
              .eq('user_id', client.id);

            // Buscar limite de dispositivos pelo plano (max_devices)
            // Mantém o valor inicial do cliente (1 para trial) se max_devices não estiver definido
            let deviceLimitVal: number | null = client.deviceLimit ?? null;
            if (client.plan && client.plan !== 'Sem Plano') {
              const { data: planRow } = await supabase
                .from('plans')
                .select('max_devices, is_custom')
                .eq('title', client.plan)
                .single();
              if (planRow) {
                // Usa max_devices do plano se disponível
                if (planRow.max_devices != null) {
                  deviceLimitVal = planRow.max_devices;
                }
                // Para planos custom sem limite definido, considera ilimitado
                if (planRow.is_custom && deviceLimitVal == null) deviceLimitVal = 0;
              }
            }

            console.log(`💰 Cliente ${client.name}: ${paymentHistory.length} pagamentos, total=${totalRevenue.toFixed(2)}`);

            // Atualizar estatísticas
            return {
              ...client,
              paymentHistory,
              totalRevenue,
              arrProjection: arrProjectionCalc,
              planChanges: client.planChanges,
              screensCount,
              screensUsed: screensCount,
              devicesUsed: (devicesData || []).filter(d => {
                const dd = d as any;
                const hasCode = !!dd.code;
                const activeFlag = dd.is_active === true || ['active', 'activated', 'true'].includes(String(dd.status).toLowerCase());
                const associated = (dd.screenId != null) || (dd.screen_id != null);
                return hasCode && (activeFlag || associated);
              }).length,
              devicesStatus: (devicesData || []).map(d => ({ id: String((d as any).id), status: String((d as any).status) })),
              promotionsUsed: (promosData || []).filter(p => (p as any).active === true).length,
              deviceLimit: deviceLimitVal,
            };
          } catch (error) {
            console.warn(`⚠️ Erro ao buscar estatísticas para cliente ${client.id}:`, error);
            return client;
          }
        })
      );

      console.log('📋 Definindo clientes no estado:', clientsWithFullStats.length, 'clientes');
      setClients(clientsWithFullStats);
      console.log('✅ Clientes carregados com sucesso:', clientsWithFullStats.length);
    } catch (error) {
      console.error('❌ Erro ao buscar clientes:', error);
      toast({
        title: "❌ Erro",
        description: "Erro ao carregar clientes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para buscar dados do plano
  const getPlanData = (planId: string) => {
    // Cache de planos para evitar múltiplas consultas
    const planCache = new Map();

    if (planCache.has(planId)) {
      return planCache.get(planId);
    }

    // Buscar plano no banco
    const fetchPlan = async () => {
      const { data: plan } = await supabase
        .from('plans')
        .select('*')
        .eq('id', planId)
        .single();

      if (plan) {
        planCache.set(planId, plan);
        return plan;
      }
      return null;
    };

    return fetchPlan();
  };

  // Função para calcular ARR e mudanças de plano
  const calculateARRProjection = (
    paymentHistory: PaymentHistory[],
    currentMRR: number,
    createdAt: string,
    subscriptionStatus: string,
    subscriptionEndDate: string | null
  ) => {
    try {
      // Se o cliente está ativo, calcular projeção anual
      if (subscriptionStatus === 'ativo' && subscriptionEndDate) {
        const endDate = new Date(subscriptionEndDate);
        const now = new Date();

        // Se o plano não expirou, calcular ARR baseado no MRR atual
        if (endDate > now) {
          return currentMRR * 12;
        }
      }

      // Se cancelado ou inativo, calcular baseado no histórico
      const totalPaid = paymentHistory
        .filter(p => p.status === 'pago')
        .reduce((sum, p) => sum + p.amount, 0);

      // Calcular média mensal dos pagamentos realizados
      const paidPayments = paymentHistory.filter(p => p.status === 'pago');
      if (paidPayments.length > 0) {
        const averageMonthly = totalPaid / paidPayments.length;
        return averageMonthly * 12;
      }

      return 0;
    } catch (error) {
      console.error('❌ Erro ao calcular ARR:', error);
      return 0;
    }
  };

  const calculateTotalRevenue = (
    paymentHistory: PaymentHistory[],
    subscriptionStatus: string
  ) => {
    try {
      let totalRevenue = paymentHistory
        .reduce((sum, p) => sum + p.amount, 0);
      return totalRevenue;
    } catch (error) {
      console.error('❌ Erro ao calcular receita total:', error);
      return 0;
    }
  };

  // Função para buscar histórico de pagamentos do cliente
  const fetchPaymentHistory = async (userId: string): Promise<PaymentHistory[]> => {
    try {
      console.log('🔍 Buscando histórico de pagamentos para usuário:', userId);
      const histories: PaymentHistory[] = [];

      // payments
      const { data: payments, error: payErr } = await supabase
        .from('payments')
        .select('created_at, amount, status, description')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (payErr) {
        console.warn('⚠️ Falha ao buscar payments, continuando com eventos:', payErr);
      } else {
        console.log('💰 Pagamentos encontrados na tabela payments:', payments?.length || 0);
        (payments || []).forEach((p: any) => {
          const statusRaw = String(p.status || '').toLowerCase();
          const descRaw = String(p.description || '').toLowerCase();
          const isManualPaid = statusRaw === 'manual' || statusRaw === 'manual_paid' || statusRaw === 'reactivated' || statusRaw === 'active' || descRaw.includes('manual');
          const isPaid = statusRaw === 'completed' || statusRaw === 'paid' || isManualPaid;
          histories.push({
            date: p.created_at,
            amount: Number(p.amount || 0) / 100,
            status: isPaid ? 'pago' : 'falha',
            description: p.description || 'Pagamento de assinatura'
          });
        });
      }

      // stripe_webhook_events (sucesso)
      const { data: events, error: evtErr } = await supabase
        .from('stripe_webhook_events')
        .select('type, success, processed_at, payload')
        .eq('success', true)
        .order('processed_at', { ascending: true });
      if (evtErr) {
        console.warn('⚠️ Falha ao buscar eventos Stripe:', evtErr);
      } else {
        (events || []).forEach((e: any) => {
          const payloadUserId = e?.payload?.user_id || e?.payload?.data?.object?.customer || null;
          if (payloadUserId && String(payloadUserId) === String(userId)) {
            // tentar extrair valor
            const amountTotal = e?.payload?.amount || e?.payload?.data?.object?.amount_total || e?.payload?.data?.object?.amount_paid || 0;
            histories.push({
              date: e.processed_at,
              amount: Number(amountTotal || 0) / 100,
              status: 'pago',
              description: String(e.type || 'Stripe evento')
            });
          }
        });
      }

      // subscription_logs (manual activation como referência)
      const { data: subs } = await supabase
        .from('subscription_logs')
        .select('new_status, new_plan, changed_by, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });
      (subs || []).forEach((s: any) => {
        if (String(s.new_status || '').toLowerCase() === 'active') {
          histories.push({
            date: s.created_at,
            amount: 0,
            status: 'pago',
            description: `Ativação manual - ${s.new_plan || ''} (${s.changed_by || 'admin'})`
          });
        }
      });

      if (histories.length === 0) {
        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('subscription_status, subscription_plan, subscription_start_date, plan_price, created_at')
            .eq('id', userId)
            .single();
          if (profile) {
            histories.push({
              date: profile.subscription_start_date || profile.created_at,
              amount: Number(profile.plan_price || 0),
              status: (String(profile.subscription_status || '').toLowerCase() === 'active') ? 'pago' : 'falha',
              description: `Assinatura iniciada - ${profile.subscription_plan || 'Sem plano'}`
            });
          }
        } catch { }
      }
      // Ordenar em ordem decrescente por data
      histories.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      return histories;
    } catch (error) {
      console.error('❌ Erro ao buscar histórico de pagamentos:', error);
      return [];
    }
  };

  // Carregar clientes e planos quando o componente montar
  React.useEffect(() => {
    console.log('🔄 useEffect executado - carregando clientes...');
    fetchRealClients();
    fetchPlans();
  }, []);

  // Detectar plano custom ao abrir modal, e preencher valores existentes
  React.useEffect(() => {
    if (!selectedClient) return;
    const titleLc = (selectedClient.plan || '').toLowerCase();
    const isCustom = titleLc.includes('personalite') || titleLc.includes('personalizado') || titleLc.includes('custom');
    setIsCustomSelected(isCustom);
    if (isCustom) {
      if (selectedClient.deviceLimit != null) setCustomDeviceLimit(String(selectedClient.deviceLimit));
      if (selectedClient.promoLimit != null) setCustomQtCupom(String(selectedClient.promoLimit));
      if (selectedClient.mrr != null) setCustomPrice(String(Number(selectedClient.mrr).toFixed(2)).replace('.', ','));
    }
  }, [selectedClient]);

  React.useEffect(() => {
    const refreshSelectedClient = async () => {
      if (!selectedClient) return;
      const userId = selectedClient.id;
      const paymentHistory: PaymentHistory[] = await fetchPaymentHistory(userId);
      let devicesData: any[] = [];
      try {
        const { data: devRpc } = await supabase.rpc('admin_get_user_devices', { p_user_id: userId });
        if (devRpc && Array.isArray(devRpc)) devicesData = devRpc as any[];
      } catch { }
      const { data: promosData } = await supabase
        .from('promos')
        .select('id, active')
        .eq('user_id', userId);
      setSelectedClient(prev => prev ? {
        ...prev,
        paymentHistory,
        screensUsed: 0,
        devicesUsed: (devicesData || []).filter((d: any) => {
          const hasCode = !!d.code;
          const activeFlag = d.is_active === true || ['active', 'activated', 'true'].includes(String(d.status).toLowerCase());
          const associated = (d.screenId != null) || (d.screen_id != null);
          return hasCode && (activeFlag || associated);
        }).length,
        devicesStatus: (devicesData || []).map((d: any) => ({ id: String(d.id), status: String(d.status) })),
        promotionsUsed: (promosData || []).filter((p: any) => p.active === true).length
      } : prev);
    };
    refreshSelectedClient();
  }, [selectedClient?.id]);

  // Função para buscar planos disponíveis
  const fetchPlans = async () => {
    try {
      const { data: plansData, error } = await supabase
        .from('plans')
        .select('id, title, price, description, is_custom, device_limit, max_devices, QT_cupom')
        .order('created_at', { ascending: true });

      if (error) throw error;
      setPlans(plansData || []);
    } catch (error) {
      console.error('Erro ao buscar planos:', error);
      toast({
        title: "❌ Erro",
        description: "Erro ao carregar planos disponíveis",
        variant: "destructive",
      });
    }
  };

  // Função para atualizar o plano e status do cliente
  const updateClientSubscription = async (clientId: string, planId: string, isActive: boolean, overrides?: { device_limit?: number; qt_cupom?: number; price?: number }) => {
    try {
      const selectedPlanData = plans.find(p => p.id === planId);
      if (!selectedPlanData) throw new Error('Plano não encontrado');

      // Criar data no fuso horário de Brasília
      const brasiliaDate = new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' });
      const startDate = new Date(brasiliaDate);

      // Buscar dados completos do plano para expiração e cotas
      const { data: fullPlan } = await supabase
        .from('plans')
        .select('title, price, expiration_days, expiration_minutes, QT_cupom, is_custom')
        .eq('id', planId)
        .single();

      // Calcular data de expiração
      const expirationDate = new Date(startDate);
      const days = Number(fullPlan?.expiration_days || 0);
      const minutes = Number(fullPlan?.expiration_minutes || 0);
      if (minutes > 0) {
        expirationDate.setMinutes(expirationDate.getMinutes() + minutes);
      } else {
        expirationDate.setDate(expirationDate.getDate() + (days > 0 ? days : 30));
      }

      const { data: beforeProfile } = await supabase
        .from('profiles')
        .select('subscription_plan, subscription_status')
        .eq('id', clientId)
        .single();

      const qtCupomToSet = overrides?.qt_cupom != null ? overrides.qt_cupom : (fullPlan?.QT_cupom ?? null);
      const priceToSet = overrides?.price != null ? overrides.price : Number(fullPlan?.price ?? selectedPlanData.price);

      // Se plano é custom, persistir limites no próprio plano
      if (fullPlan?.is_custom && overrides) {
        try {
          await supabase
            .from('plans')
            .update({
              device_limit: overrides.device_limit,
              QT_cupom: overrides.qt_cupom
            })
            .eq('id', planId);
        } catch (e) {
          console.warn('⚠️ Falha ao atualizar limites no plano custom:', e);
        }
      }

      // Verificar se o plano sendo ativado é Trial (para resetar has_used_trial quando admin master ativar manualmente)
      const isTrialPlan = (fullPlan?.title || selectedPlanData.title).toLowerCase().includes('trial');

      const { error } = await supabase
        .from('profiles')
        .update({
          subscription_plan: fullPlan?.title || selectedPlanData.title,
          subscription_status: isActive ? 'active' : 'inactive',
          subscription_expires_at: expirationDate.toISOString(),
          subscription_start_date: startDate.toISOString(),
          subscription_end_date: expirationDate.toISOString(),
          plan_price: priceToSet,
          QT_cupom_User: qtCupomToSet,
          // Se admin master está ativando Trial, resetar has_used_trial para permitir uso
          // O usuário poderá usar o Trial novamente após esta ativação manual
          ...(isTrialPlan && isActive ? { has_used_trial: false } : {})
        })
        .eq('id', clientId);

      if (error) throw error;

      // Atualizar estado local imediatamente para refletir mudanças
      setIsSubscriptionActive(isActive);
      setSelectedClient((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          plan: fullPlan?.title || selectedPlanData.title,
          status: isActive ? 'ativo' : 'cancelado',
          mrr: priceToSet,
          createdAt: startDate.toISOString(),
          planExpirationDate: expirationDate.toISOString(),
          deviceLimit: overrides?.device_limit ?? prev.deviceLimit ?? null,
          promoLimit: qtCupomToSet ?? prev.promoLimit ?? null,
        };
      });

      // Registrar histórico de alteração
      await supabase
        .from('subscription_logs')
        .insert({
          user_id: clientId,
          old_plan: beforeProfile?.subscription_plan || null,
          new_plan: fullPlan?.title || selectedPlanData.title,
          old_status: beforeProfile?.subscription_status || null,
          new_status: isActive ? 'active' : 'inactive',
          changed_by: 'admin'
        });

      if (fullPlan?.is_custom || overrides?.price != null) {
        try {
          const amountCents = Math.round(priceToSet * 100);
          await PaymentService.recordPayment({
            user_id: clientId,
            plan_id: planId,
            amount: amountCents,
            status: 'completed',
            payment_method: 'manual',
            description: `Plano Personalizado - master: ${masterUser?.email || 'admin'}`,
            metadata: { source: 'manual', by: masterUser?.email || 'admin' }
          });
          await supabase
            .from('stripe_webhook_events')
            .insert({
              type: isActive ? 'manual.activation' : 'manual.deactivation',
              event_id: `manual_${clientId}_${Date.now()}`,
              payload: {
                user_id: clientId,
                plan_id: planId,
                by: masterUser?.email || 'admin',
                overrides: overrides || {},
                status: isActive ? 'active' : 'inactive'
              },
              success: true,
              processed_at: new Date().toISOString(),
              retries: 0
            });
        } catch (payErr) {
          console.warn('⚠️ Erro ao registrar pagamento manual:', payErr);
        }
      }

      toast({
        title: "✅ Sucesso",
        description: "Assinatura atualizada com sucesso",
        variant: "default",
      });

      fetchRealClients();
    } catch (error) {
      console.error('Erro ao atualizar assinatura:', error);
      toast({
        title: "❌ Erro",
        description: "Erro ao atualizar assinatura",
        variant: "destructive",
      });
    }
  };

  const handleDeletePlan = async () => {
    if (!selectedClient) return;

    setIsDeletingPlan(true);
    try {
      console.log('🔄 Iniciando processo de deletar plano para:', selectedClient.email);
      console.log('📋 Dados do cliente:', {
        id: selectedClient.id,
        email: selectedClient.email,
        name: selectedClient.name,
        plan: selectedClient.plan,
        status: selectedClient.status
      });

      // Verificar se a função deleteUserPlan está disponível
      if (typeof deleteUserPlan !== 'function') {
        throw new Error('Função deleteUserPlan não está disponível');
      }

      console.log('🚀 Chamando deleteUserPlan...');
      const result = await deleteUserPlan(
        selectedClient.id,
        selectedClient.email
      );

      console.log('✅ Plano deletado com sucesso:', result);

      // Fechar modal e recarregar dados
      setDeletePlanDialogOpen(false);
      setSelectedClient(null);

      // Mostrar mensagem de sucesso
      toast({
        title: "✅ Plano Deletado",
        description: `Plano do cliente ${selectedClient.name} foi deletado com sucesso!`,
        variant: "default",
      });

      // Recarregar dados do dashboard
      fetchRealClients();
    } catch (error) {
      console.error('❌ Erro ao deletar plano:', error);
      toast({
        title: '❌ Erro',
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: 'destructive'
      });
    } finally {
      setIsDeletingPlan(false);
    }
  };

  return (
    <section className="mb-10">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <h2 className="text-xl font-bold">Gerenciamento de Clientes</h2>
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por nome ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        {loading ? (
          <div className="w-full h-32 flex items-center justify-center text-gray-500">Carregando clientes...</div>
        ) : clients.length === 0 ? (
          <div className="text-gray-600">Nenhum cliente encontrado</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>Plano</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Data de Assinatura</TableHead>
                <TableHead>Data de Vencimento</TableHead>
                <TableHead>MRR</TableHead>
                <TableHead>ARR</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients
                .filter(client =>
                  client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  client.email.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center border border-gray-200">
                          {c.avatar_url ? (
                            <img
                              src={c.avatar_url}
                              alt={c.name}
                              className="h-10 w-10 object-cover"
                            />
                          ) : (
                            <span className="text-gray-500 font-bold text-lg">
                              {c.name?.charAt(0).toUpperCase() || c.email?.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{c.name}</div>
                          <div className="text-xs text-gray-500">{c.email}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{c.plan}</TableCell>
                    <TableCell>
                      {(() => {
                        // Verificar se é plano TRIAL para mostrar 'Testando'
                        const isTrialPlan = c.plan && (c.plan.toLowerCase().includes('trial') || c.plan.toLowerCase().includes('gratuito'));
                        // Para Trial, verificar se ainda está ativo (não expirado)
                        // c.status já considera a data de expiração na lógica de fetchRealClients
                        const isTrialActive = isTrialPlan && c.status === 'ativo';
                        const displayStatus = isTrialActive ? 'testando' : c.status;
                        return (
                          <Badge className={statusColor[displayStatus] || ''}>
                            {paymentStatusLabel[displayStatus] || displayStatus}
                          </Badge>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {c.createdAt ? (
                        <span className="text-sm">
                          {new Date(c.createdAt).toLocaleDateString('pt-BR')}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Sem data</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {c.planExpirationDate ? (
                        <span className="text-sm">
                          {new Date(c.planExpirationDate).toLocaleDateString('pt-BR')}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Sem vencimento</span>
                      )}
                    </TableCell>
                    <TableCell>R$ {Number(c.mrr || 0).toFixed(2)}</TableCell>
                    <TableCell>R$ {Number(c.arrProjection || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Button variant="outline" onClick={() => handleClientSelect(c)}>Ver detalhes</Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        )}
      </div>

      {!!selectedClient && (
        <Dialog open={!!selectedClient} onOpenChange={() => setSelectedClient(null)}>
          <DialogContent hideClose className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-blue-600" /> Cliente</DialogTitle>
              <div className="flex items-center justify-between">
                <DialogDescription className="mt-2">{selectedClient?.name} • {selectedClient?.email}</DialogDescription>
                {masterUser?.email && masterUser.email.toLowerCase().includes('admin') && selectedClient && (
                  <Button
                    variant="outline"
                    className="gap-2 ml-4"
                    onClick={async () => {
                      try {
                        console.log('🔍 Buscando organização para cliente:', selectedClient.id);
                        let orgId: string | undefined = undefined;

                        // Tentativa 1: Buscar via organizations_users
                        const { data: orgMap, error: orgMapError } = await supabase
                          .from('organizations_users')
                          .select('organization_id')
                          .eq('user_id', selectedClient.id)
                          .limit(1);

                        console.log('🔍 organizations_users resultado:', { orgMap, orgMapError });

                        if (orgMap && orgMap[0]?.organization_id) {
                          orgId = orgMap[0].organization_id as string;
                          console.log('✅ Organização encontrada via organizations_users:', orgId);
                        } else {
                          console.log('⚠️ Organização não encontrada em organizations_users, tentando RPC...');

                          // Tentativa 2: Buscar via RPC admin_get_user_devices
                          const { data: devicesRpc, error: devRpcError } = await supabase
                            .rpc('admin_get_user_devices', { p_user_id: selectedClient.id });

                          console.log('🔍 admin_get_user_devices resultado:', { devicesRpc, devRpcError });

                          if (devicesRpc && Array.isArray(devicesRpc) && devicesRpc.length > 0) {
                            const deviceWithOrg = (devicesRpc as any[]).find(d => d.organization_id);
                            if (deviceWithOrg) {
                              orgId = String(deviceWithOrg.organization_id);
                              console.log('✅ Organização encontrada via devices:', orgId);
                            }
                          }

                          // Tentativa 3: Buscar via RPC admin_get_user_screens
                          if (!orgId) {
                            console.log('⚠️ Organização não encontrada em devices, tentando screens...');
                            const { data: screensRpc, error: scrRpcError } = await supabase
                              .rpc('admin_get_user_screens', { p_user_id: selectedClient.id });

                            console.log('🔍 admin_get_user_screens resultado:', { screensRpc, scrRpcError });

                            if (screensRpc && Array.isArray(screensRpc) && screensRpc.length > 0) {
                              const first = (screensRpc as any[])[0] as any;
                              if (first && first.organization_id) {
                                orgId = String(first.organization_id);
                                console.log('✅ Organização encontrada via screens:', orgId);
                              }
                            }
                          }

                          // Tentativa 4: Usar organização padrão
                          if (!orgId) {
                            console.log('⚠️ Nenhuma organização encontrada, usando padrão');
                            orgId = 'fe3bd8b7-d5ad-4cb4-8ee6-cbf0a3a5b2d2'; // Organização padrão

                            // Tentar vincular o usuário à organização padrão
                            try {
                              await supabase
                                .from('organizations_users')
                                .insert({
                                  user_id: selectedClient.id,
                                  organization_id: orgId,
                                  role: 'admin'
                                });
                              console.log('✅ Usuário vinculado à organização padrão');
                            } catch (insertError) {
                              console.warn('⚠️ Não foi possível vincular à organização padrão:', insertError);
                            }
                          }
                        }

                        const sc = Number(selectedClient?.screensCount || 0);
                        const url = `/dashboard?impersonate=${selectedClient.id}&org=${orgId}&imp=1&sc=${sc}`;

                        console.log('🚀 Abrindo dashboard do cliente:', {
                          clientId: selectedClient.id,
                          orgId,
                          url
                        });

                        window.open(url, '_blank', 'noopener,noreferrer');
                      } catch (e) {
                        console.error('❌ Erro ao abrir dashboard:', e);
                        toast({ title: 'Erro ao abrir dashboard', description: 'Não foi possível abrir o dashboard do cliente.', variant: 'destructive' });
                      }
                    }}
                  >
                    <LayoutDashboard className="w-4 h-4" /> Dashboard cliente
                  </Button>
                )}
              </div>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="col-span-2">
                  <Label htmlFor="plan-select" className="mb-2 block text-gray-700">Plano Atual</Label>
                  <Select
                    value={(selectedPlan || plans.find(p => p.title === selectedClient?.plan)?.id || '')}
                    onValueChange={(value) => {
                      setSelectedPlan(value);
                      const plan = plans.find(p => p.id === value);
                      const titleLc = (plan?.title || '').toLowerCase();
                      const isCustom = !!plan?.is_custom || titleLc.includes('personalizado') || titleLc.includes('custom') || titleLc.includes('personalite');
                      setIsCustomSelected(isCustom);
                      setCustomErrors({});
                      if (!isCustom && selectedClient) {
                        updateClientSubscription(selectedClient.id, value, isSubscriptionActive);
                      }
                    }}
                  >
                    <SelectTrigger id="plan-select">
                      <SelectValue>
                        {plans.find(p => p.id === (selectedPlan || plans.find(pp => pp.title === selectedClient?.plan)?.id || ''))?.title || selectedClient?.plan || 'Nenhum plano'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {plans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.title} - R$ {plan.price.toFixed(2)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {isCustomSelected && (
                  <div className="col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4 mt-2 p-3 bg-gray-50 rounded-lg border">
                    <div>
                      <Label htmlFor="custom-device">Limite de dispositivos</Label>
                      <input
                        id="custom-device"
                        type="number"
                        min={1}
                        value={customDeviceLimit}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9]/g, '');
                          setCustomDeviceLimit(v);
                          setCustomErrors((prev) => ({ ...prev, device: !v || parseInt(v) < 1 ? 'Informe um inteiro positivo' : '' }));
                        }}
                        className={`w-full px-3 py-2 border rounded-md ${customErrors.device ? 'border-red-500' : ''}`}
                        title="Número máximo de dispositivos permitidos para este plano personalizado"
                      />
                      {customErrors.device && <p className="text-xs text-red-600 mt-1">{customErrors.device}</p>}
                    </div>
                    <div>
                      <Label htmlFor="custom-qt">Quantidade de promoções permitidas</Label>
                      <input
                        id="custom-qt"
                        type="number"
                        min={0}
                        value={customQtCupom}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9]/g, '');
                          setCustomQtCupom(v);
                          setCustomErrors((prev) => ({ ...prev, qt: v === '' ? 'Informe um inteiro (0 ou maior)' : '' }));
                        }}
                        className={`w-full px-3 py-2 border rounded-md ${customErrors.qt ? 'border-red-500' : ''}`}
                        title="Número máximo de promoções que o usuário poderá criar"
                      />
                      {customErrors.qt && <p className="text-xs text-red-600 mt-1">{customErrors.qt}</p>}
                    </div>
                    <div>
                      <Label htmlFor="custom-price">Valor do plano personalizado (R$)</Label>
                      <input
                        id="custom-price"
                        type="text"
                        inputMode="decimal"
                        value={customPrice}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9.,]/g, '');
                          setCustomPrice(v);
                          const num = parseFloat(v.replace(',', '.'));
                          setCustomErrors((prev) => ({ ...prev, price: isNaN(num) || num <= 0 ? 'Informe um valor monetário válido' : '' }));
                        }}
                        className={`w-full px-3 py-2 border rounded-md ${customErrors.price ? 'border-red-500' : ''}`}
                        title="Valor cobrado pelo plano personalizado"
                      />
                      {customErrors.price && <p className="text-xs text-red-600 mt-1">{customErrors.price}</p>}
                    </div>
                    <div className="md:col-span-3 flex justify-end">
                      <Button
                        onClick={async () => {
                          if (!selectedClient) return;
                          const resolvedPlanId = selectedPlan || plans.find(pp => pp.title === selectedClient.plan)?.id || '';
                          if (!resolvedPlanId) {
                            toast({ title: 'Plano não encontrado', description: 'Selecione um plano válido antes de aplicar.', variant: 'destructive' });
                            return;
                          }
                          const d = parseInt(customDeviceLimit);
                          const q = parseInt(customQtCupom);
                          const p = parseFloat(customPrice.replace(',', '.'));
                          const errs = {
                            device: !d || d < 1 ? 'Informe um inteiro positivo' : '',
                            qt: isNaN(q) || q < 0 ? 'Informe um inteiro (0 ou maior)' : '',
                            price: isNaN(p) || p <= 0 ? 'Informe um valor monetário válido' : ''
                          };
                          setCustomErrors(errs);
                          if (errs.device || errs.qt || errs.price) {
                            toast({ title: 'Validação', description: 'Corrija os campos destacados', variant: 'destructive' });
                            return;
                          }
                          await updateClientSubscription(selectedClient.id, resolvedPlanId, true, { device_limit: d, qt_cupom: q, price: p });
                        }}
                      >
                        Salvar plano personalizado
                      </Button>
                    </div>
                  </div>
                )}
                <div className="col-span-2 flex items-center gap-3 mt-4">
                  <div className="flex items-center gap-2">
                    <Toggle
                      checked={isSubscriptionActive}
                      onChange={async (newActive) => {
                        if (!selectedClient) return;
                        const resolvedPlanId = selectedPlan || plans.find(pp => pp.title === selectedClient.plan)?.id || '';
                        if (!resolvedPlanId) {
                          toast({ title: 'Plano não encontrado', description: 'Selecione um plano válido antes de ativar.', variant: 'destructive' });
                          return;
                        }
                        setIsSubscriptionActive(newActive);
                        await updateClientSubscription(selectedClient.id, resolvedPlanId, newActive);
                      }}
                      className={isSubscriptionActive ? "bg-green-500" : "bg-gray-300"}
                      aria-label="Ativar ou desativar assinatura"
                    />
                    <Label className="text-base font-medium text-gray-700">Assinatura Ativa</Label>
                  </div>
                </div>

                <div className="col-span-2 grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Limite de Aparelhos</p>
                    <p className="text-lg font-semibold">
                      {selectedClient?.deviceLimit === 0 ? 'Ilimitado' : (selectedClient?.deviceLimit ?? 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Aparelhos Ativos</p>
                    <p className="text-lg font-semibold">{selectedClient?.devicesUsed ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Promoções permitidas</p>
                    <p className="text-lg font-semibold">{selectedClient?.promoLimit ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Promoções ativas</p>
                    <p className="text-lg font-semibold">{selectedClient?.promotionsUsed ?? 0}</p>
                  </div>
                </div>
              </div>
              <div>
                <span className="font-semibold">Histórico de Pagamentos:</span>
                <div className="flex items-center gap-2 mt-2">
                  <input type="date" value={filterStart} onChange={(e) => setFilterStart(e.target.value)} className="border rounded px-2 py-1 text-sm" />
                  <input type="date" value={filterEnd} onChange={(e) => setFilterEnd(e.target.value)} className="border rounded px-2 py-1 text-sm" />
                  <Select value={filterType} onValueChange={(v) => setFilterType(v as any)}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="pago">Pago</SelectItem>
                      <SelectItem value="falha">Falha</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className={selectedClient.paymentHistory.length > 6 ? 'mt-2 max-h-64 overflow-y-auto' : 'mt-2'}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Valor</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Descrição</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedClient.paymentHistory.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-gray-500">
                            Nenhum pagamento registrado
                          </TableCell>
                        </TableRow>
                      ) : (
                        selectedClient.paymentHistory
                          .filter(p => {
                            if (filterType !== 'all' && p.status !== filterType) return false;
                            if (filterStart && new Date(p.date) < new Date(filterStart)) return false;
                            if (filterEnd && new Date(p.date) > new Date(filterEnd)) return false;
                            return true;
                          })
                          .map((p, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{new Date(p.date).toLocaleDateString('pt-BR')}</TableCell>
                              <TableCell>R$ {(p.amount || 0).toFixed(2)}</TableCell>
                              <TableCell>
                                {p.status === 'pago' ? (
                                  <Badge variant="default" className="bg-green-100 text-green-700 flex items-center gap-1"><TrendingUp className="w-4 h-4" /> Pago</Badge>
                                ) : (
                                  <Badge variant="secondary" className="bg-yellow-100 text-yellow-700 flex items-center gap-1"><TrendingDown className="w-4 h-4" /> Falha</Badge>
                                )}
                              </TableCell>
                              <TableCell>{p.description || '-'}</TableCell>
                            </TableRow>
                          ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <DialogClose asChild>
                  <Button variant="outline">Fechar</Button>
                </DialogClose>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
};

export default ClientFinanceSection;
